import * as React from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import { Visibility } from "@mui/icons-material";
import { VisibilityOff } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { pick } from "lodash";
import { API_BASE_URL } from "../../config";
import { useNavigate } from "react-router-dom";
import useLogin from "../../hooks/useLogin";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useTokens } from "../../theme";

export default function ResetPasswordForm() {
  const { loginProps } = useLogin();
  const { showPassword, loginHandlers } = loginProps;
  const [isLoading, setIsLoading] = React.useState(false);

  const [resetForm, setResetForm] = React.useState({
    resetToken: "",
    newPassword: "",
    confirmPassword: "",
  });

  const { alert } = useAlert();
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();
  const tokens = useTokens();

  const resetToken = queryParams.get("resetToken");

  useEffect(() => {
    setResetForm({ resetToken: resetToken, newPassword: "" });
  }, [resetToken]);

  const handleInputChange = (field, value) => {
    setResetForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetPassword = async () => {
    if (!resetToken) {
      alert({
        title: "Failed to Reset Password",
        detail: "This reset link is missing its token. Request a new one from the login page.",
        severity: "error",
        duration: 8000,
      });
      return;
    }

    if (resetForm.confirmPassword !== resetForm.newPassword) {
      alert({
        title: "Failed to Reset Password",
        detail: `Passwords do not match`,
        severity: "error",
        duration: 5000,
      });
      return;
    }

    const payload = pick(resetForm, ["resetToken", "newPassword"]);

    const options = {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...payload }),
    };

    setIsLoading(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/agents/reset-password/v1`,
        options
      );

      const result = await response.json();

      if (result.status !== "SUCCESS") {
        alert({
          title: "Failed to Reset Password",
          detail: result.failureReason,
          severity: "warning",
          duration: 5000,
        });
        return;
      }

      alert({
        title: "Password Reset",
        detail: `Password was successfully reset. You can now sign in.`,
        severity: "success",
        duration: 5000,
      });
      navigate("/");
    } catch (error) {
      alert({
        title: "Error Occurred",
        detail: "Unable to send reset password at the moment",
        severity: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      noValidate
      onSubmit={async (event) => {
        event.preventDefault();
        await resetPassword();
      }}
    >
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography
          component="h2"
          sx={{ fontSize: "1.25rem", fontWeight: 600, color: tokens.foreground }}
        >
          Reset password
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: tokens.mutedForeground }}>
          Choose a new password for your account.
        </Typography>
      </Stack>
      {!resetToken && (
        <Alert severity="error" sx={{ mb: 2 }}>
          This reset link is missing its token. Request a new link from the login page.
        </Alert>
      )}
      <Stack spacing={2}>
        <TextField
          id="reset-new-password"
          label="Password"
          variant="outlined"
          fullWidth
          autoComplete="new-password"
          type={showPassword ? "text" : "password"}
          onChange={(event) =>
            handleInputChange("newPassword", event.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          id="reset-confirm-password"
          label="Confirm Password"
          variant="outlined"
          fullWidth
          autoComplete="new-password"
          type={showPassword ? "text" : "password"}
          onChange={(event) =>
            handleInputChange("confirmPassword", event.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  aria-pressed={showPassword}
                  onClick={loginHandlers.toggle}
                  onMouseDown={loginHandlers.mouseDown}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Stack spacing={1.5} sx={{ mt: 3 }}>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          size="large"
          type="submit"
          fullWidth
          disabled={!resetToken}
          startIcon={<LockResetIcon />}
        >
          Reset Password
        </LoadingButton>
        <Button variant="outlined" size="large" fullWidth onClick={() => navigate("/")}>
          Return to Login
        </Button>
      </Stack>
    </Box>
  );
}
